import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useApplyBranding } from '@/lib/branding';
import Layout from '@/components/Layout';
import MarketingLayout from '@/components/MarketingLayout';
import ProtectedRoute from '@/components/ProtectedRoute';

import Login from '@/pages/Login';

// Marketing — lazy loaded so toolContent.js only loads on /tools routes
const Home           = lazy(() => import('@/pages/marketing/Home'));
const Tools          = lazy(() => import('@/pages/marketing/Tools'));
const ToolPage       = lazy(() => import('@/pages/marketing/ToolPage'));
const Features       = lazy(() => import('@/pages/marketing/Features'));
const Pricing        = lazy(() => import('@/pages/marketing/Pricing'));
const ForDistricts   = lazy(() => import('@/pages/marketing/ForDistricts'));
const AcademyMarketing = lazy(() => import('@/pages/marketing/Academy'));
const About          = lazy(() => import('@/pages/marketing/About'));
const Contact        = lazy(() => import('@/pages/marketing/Contact'));

// App — lazy loaded
const Dashboard      = lazy(() => import('@/pages/Dashboard'));
const ToolGrid       = lazy(() => import('@/pages/teacher-suite/ToolGrid'));
const ToolDetail     = lazy(() => import('@/pages/teacher-suite/ToolDetail'));
const RunHistory     = lazy(() => import('@/pages/teacher-suite/RunHistory'));
const SparkBot       = lazy(() => import('@/pages/SparkBot'));
const Academy        = lazy(() => import('@/pages/Academy'));
const Profile        = lazy(() => import('@/pages/Profile'));
const Notifications  = lazy(() => import('@/pages/Notifications'));
const UserManagement = lazy(() => import('@/pages/admin/UserManagement'));
const ISDManagement  = lazy(() => import('@/pages/admin/ISDManagement'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="w-6 h-6 rounded-full border-2 border-purple-300 border-t-purple-600 animate-spin" />
    </div>
  );
}

export default function App() {
  useApplyBranding();
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public marketing site */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/tools/:slug" element={<ToolPage />} />
          <Route path="/academy" element={<AcademyMarketing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/for-districts" element={<ForDistricts />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
        </Route>

        {/* Auth */}
        <Route path="/login" element={<Login />} />

        {/* Authenticated app */}
        <Route element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/notifications" element={<Notifications />} />

            {/* Teacher Suite — teachers and above */}
            <Route element={<ProtectedRoute allow={['teacher', 'school_admin', 'isd_admin', 'super_admin']} />}>
              <Route path="/teacher-suite" element={<ToolGrid />} />
              <Route path="/teacher-suite/history" element={<RunHistory />} />
              <Route path="/teacher-suite/:slug" element={<ToolDetail />} />
              <Route path="/analytics" element={<RunHistory />} />
            </Route>

            {/* Student-only */}
            <Route element={<ProtectedRoute allow={['student']} />}>
              <Route path="/sparkbot" element={<SparkBot />} />
              <Route path="/learn" element={<Academy />} />
            </Route>

            {/* User management — school_admin and above */}
            <Route element={<ProtectedRoute allow={['school_admin', 'isd_admin', 'super_admin']} />}>
              <Route path="/admin/users" element={<UserManagement />} />
            </Route>

            {/* Super-admin only */}
            <Route element={<ProtectedRoute allow={['super_admin']} />}>
              <Route path="/admin/isds" element={<ISDManagement />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
